import React from 'react';

import orderBy from 'lodash.orderby';
import type { FieldValues } from 'react-hook-form';

import type { RAsyncItemsRendererProps } from '@/_components';
import { Table, TableBody } from '@/_components';
import type { QuickAddSectionProps, QuickItemRenderer } from '@/_containers';
import { NoDataMessage, SyncQuickAddSection } from '@/_containers';
import { nameResolver } from '@/_shared';

import { ItemWithPhoneNumber } from './item-with-phone-number';

import type { ContactLinkerWithPhoneNumberQuerySchema } from '../../schemas';

type ContactSyncMultiSelectProps<TFormValues extends FieldValues = FieldValues> = Pick<
  QuickAddSectionProps<ContactLinkerWithPhoneNumberQuerySchema, TFormValues>,
  'isEditable' | 'control' | 'fieldName' | 'title' | 'sectionTitle' | 'icon' | 'rightSlot'
> & {
  items: ContactLinkerWithPhoneNumberQuerySchema[];
  options: ContactLinkerWithPhoneNumberQuerySchema[];
  showHeader?: boolean;
  sortable?: boolean; // Sortable by drag n drop
  requireEmail?: boolean;
};

export function SyncContactMultiSelectWithPhoneNumber<TFormValues extends FieldValues = FieldValues>({
  title = 'Contacts',
  sectionTitle = 'Contacts',
  items = [],
  sortable = false,
  requireEmail = false,
  ...rest
}: ContactSyncMultiSelectProps<TFormValues>) {
  const renderItem: QuickItemRenderer<ContactLinkerWithPhoneNumberQuerySchema> = React.useCallback(
    (item, { isEditable, removeItem }) => (
      <ItemWithPhoneNumber
        key={item.id}
        item={item}
        isEditable={isEditable}
        sortable={sortable}
        removeItemHandler={removeItem}
        requireEmail={requireEmail}
      />
    ),
    [requireEmail, sortable]
  );

  const renderTableItems: RAsyncItemsRendererProps<ContactLinkerWithPhoneNumberQuerySchema> = React.useCallback(
    ({ values, removeItem }) => {
      if (values.length === 0) {
        return <NoDataMessage message="No Contacts" />;
      }

      return (
        <Table containerClassName="overflow-x-auto">
          <TableBody>
            {values.map((item) => (
              <ItemWithPhoneNumber
                key={item.id}
                item={item}
                isEditable={Boolean(rest.isEditable)}
                sortable={false}
                asTableRow
                removeItemHandler={removeItem}
                requireEmail={requireEmail}
              />
            ))}
          </TableBody>
        </Table>
      );
    },
    [requireEmail, rest.isEditable]
  );

  const itemToString = React.useCallback(
    (item: ContactLinkerWithPhoneNumberQuerySchema | null) =>
      item ? nameResolver(item.first_name, item.last_name) : '',
    []
  );

  const sortValues = React.useCallback(
    // TO SEE THE DIFFERENCE, CHANGE 'name' TO 'itemToString'
    (values: ContactLinkerWithPhoneNumberQuerySchema[]) => orderBy(values, itemToString),
    [itemToString]
  );

  const keyExtractor = React.useCallback((item: ContactLinkerWithPhoneNumberQuerySchema) => item.id.toString(), []);

  const filterOptions = React.useCallback(
    (item: ContactLinkerWithPhoneNumberQuerySchema) => items.some((i) => i.id === item.id),
    [items]
  );

  const sortedItems = React.useMemo(() => (sortable ? items : sortValues(items)), [items, sortValues, sortable]);

  return (
    <SyncQuickAddSection
      {...rest}
      sectionTitle={sectionTitle}
      title={title}
      emptyDataMessage="No Contacts"
      items={sortedItems}
      keyExtractor={keyExtractor}
      itemToString={itemToString}
      renderItem={renderItem}
      customRenderItems={renderTableItems}
      sortValues={sortable ? undefined : sortValues}
      filterOptions={filterOptions}
      sortable={sortable}
    />
  );
}
